import { promises as asyncFs } from 'fs';
import path from 'path';
import { GetStaticPaths, GetStaticProps } from 'next';

import { fileExists } from '@collab/utils/files';
import { generatedPostsFolder as postsFolder } from '@collab/utils/generate/contentPaths';

import getPosts from 'libs/cms/parse/getPosts';
import { serializeMdx } from 'libs/mdx/mdxSerializer';
import Post, { type NewsPostProps } from 'pages/Post';

const formattedDate = (date: string) =>
  new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

export const getStaticPaths: GetStaticPaths = async () => {
  const posts = await getPosts();

  return {
    paths: posts.map((post) => post.href),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<NewsPostProps> = async ({
  params,
}) => {
  if (!params) {
    return { notFound: true };
  }

  const postPath = path.join(postsFolder, `${params.slug}.mdx`);
  const postsExists = await fileExists(postPath);

  if (!postsExists) {
    return { notFound: true };
  }

  const postFile = await asyncFs.readFile(postPath, 'utf8');
  const { compiledSource } = await serializeMdx(postFile);

  const postsMeta = await getPosts();
  const postMetaIndex = postsMeta
    .map((m) => m.href)
    .indexOf(`/news/${params.slug}`);

  if (postMetaIndex < 0) {
    return { notFound: true };
  }

  const formattedMeta = {
    ...postsMeta[postMetaIndex],
    date: formattedDate(postsMeta[postMetaIndex].date),
  };

  const prevHref = postsMeta[postMetaIndex - 1]?.href;
  const nextHref = postsMeta[postMetaIndex + 1]?.href;

  return {
    props: {
      frontmatter: formattedMeta,
      compiledSource: compiledSource,
      postNav: {
        ...(prevHref && { prevHref }),
        ...(nextHref && { nextHref }),
      },
    },
  };
};

export default Post;
